import { INSTALLATION_MODE_INTERVAL_MINUTES } from "./installationConfigUtils";
import {
  SCHEDULE_MODE_BATTERY,
  SCHEDULE_MODE_INSTALLATION,
} from "./installationScheduleModeUtils";

const MINUTES_PER_HOUR = 60;
const MAX_MINUTE = 59;
const MAX_HOUR = 23;

function expandCronField(token, max) {
  const values = new Set();
  if (!token) {
    return values;
  }

  for (const part of token.split(",")) {
    const match = part.match(/^(\*|\d+)(?:-(\d+))?(?:\/(\d+))?$/);
    if (!match) {
      continue;
    }

    const [, startToken, endToken, stepToken] = match;
    const step = stepToken ? Math.max(1, parseInt(stepToken, 10)) : 1;
    let start;
    let end;

    if (startToken === "*") {
      start = 0;
      end = max;
    } else if (endToken == null) {
      start = end = Math.min(max, parseInt(startToken, 10));
    } else {
      start = Math.min(max, parseInt(startToken, 10));
      end = Math.min(max, parseInt(endToken, 10));
    }

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      continue;
    }

    for (let value = start; value <= end; value += step) {
      values.add(value);
    }
  }

  return values;
}

function expandCronDayOfWeek(token) {
  const allDays = new Set([0, 1, 2, 3, 4, 5, 6]);
  if (!token || token === "*") {
    return allDays;
  }

  const values = new Set();

  const normalizeDay = (dayToken) => {
    const day = parseInt(dayToken, 10);
    if (!Number.isFinite(day)) {
      return null;
    }
    if (day === 7) {
      return 0;
    }
    return Math.min(6, Math.max(0, day));
  };

  for (const part of token.split(",")) {
    const match = part.match(/^(\*|\d+)(?:-(\d+))?(?:\/(\d+))?$/);
    if (!match) {
      continue;
    }

    const [, startToken, endToken, stepToken] = match;
    const step = stepToken ? Math.max(1, parseInt(stepToken, 10)) : 1;

    if (startToken === "*") {
      for (let day = 0; day <= 6; day += step) {
        values.add(day);
      }
      continue;
    }

    if (endToken == null) {
      const day = normalizeDay(startToken);
      if (day != null) {
        values.add(day);
      }
      continue;
    }

    let start = normalizeDay(startToken);
    let end = normalizeDay(endToken);
    if (start == null || end == null) {
      continue;
    }

    if (start <= end) {
      for (let day = start; day <= end; day += step) {
        values.add(day);
      }
    } else {
      for (let day = start; day <= 6; day += step) {
        values.add(day);
      }
      for (let day = 0; day <= end; day += step) {
        values.add(day);
      }
    }
  }

  return values.size > 0 ? values : allDays;
}

function cronExpressionToMinutesOfDay(cronExpression, dayFilter = "all") {
  if (!cronExpression || typeof cronExpression !== "string") {
    return [];
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) {
    return [];
  }

  const [minuteField, hourField, , , dayOfWeekField] = parts;
  const allowedDays = expandCronDayOfWeek(dayOfWeekField);

  if (dayFilter !== "all") {
    const selectedDay = Number(dayFilter);
    if (!allowedDays.has(selectedDay)) {
      return [];
    }
  }

  const minutes = expandCronField(minuteField, MAX_MINUTE);
  const hours = expandCronField(hourField, MAX_HOUR);
  if (minutes.size === 0 || hours.size === 0) {
    return [];
  }

  const minutesOfDay = [];
  for (const hour of hours) {
    for (const minute of minutes) {
      minutesOfDay.push(hour * MINUTES_PER_HOUR + minute);
    }
  }
  return minutesOfDay;
}

function countUniqueDailyCronExecutions(cronLines) {
  const uniqueMinutes = new Set();

  for (const cronLine of cronLines || []) {
    for (const minuteOfDay of cronExpressionToMinutesOfDay(cronLine, "all")) {
      uniqueMinutes.add(minuteOfDay);
    }
  }

  return uniqueMinutes.size;
}

function getProductionCronLines(cronState) {
  if (
    cronState.scheduleMode === SCHEDULE_MODE_INSTALLATION ||
    cronState.scheduleMode === SCHEDULE_MODE_BATTERY
  ) {
    return cronState.timingsSave?.length ? cronState.timingsSave : [];
  }
  return cronState.activeTimings?.length
    ? cronState.activeTimings
    : cronState.timingsSave || [];
}

export function getScheduleModePickerSubtitle(t, cronState) {
  if (cronState.scheduleMode === SCHEDULE_MODE_INSTALLATION) {
    return t("installation_schedule_picker_installation", {
      minutes: INSTALLATION_MODE_INTERVAL_MINUTES,
    });
  }

  if (cronState.scheduleMode === SCHEDULE_MODE_BATTERY) {
    return t("installation_schedule_picker_battery");
  }

  const productionLines = getProductionCronLines(cronState);
  const dailyCount = countUniqueDailyCronExecutions(productionLines);

  if (dailyCount === 0) {
    return t("installation_schedule_summary_empty");
  }

  return t("installation_schedule_picker_production", { count: dailyCount });
}
